import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { buttonVariants } from '@/components/v2/UI/Button';
import Link from 'next/link';
import MyHead from '../../components/MyHead';
import Image from 'next/future/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import VideoPlayer from '@/components/v2/VideoPlayer';
import StaticPagesCards from '@/components/v2/StaticPagesCards';
import { scrollToId } from '@/helpers/scroller';
import LinkTo from '@/components/LinkTo';

const UynnersPage: React.FC = () => {
  const { t } = useTranslation();

  const cardsWintersports = useMemo(
    () => [
      {
        src: '/v2/uynners/ghed2.jpg'
      },
      {
        src: '/v2/uynners/ghed1.jpg'
      }
    ],
    []
  );
  const cardsTrinx = useMemo(
    () => [
      {
        src: '/v2/uynners/trinx2.jpg'
      },
      {
        src: '/v2/uynners/trinx3.jpg'
      }
    ],
    []
  );
  const cardsAustria = useMemo(
    () => [
      {
        src: '/v2/uynners/au2.jpg'
      },
      {
        src: '/v2/uynners/au3.jpg'
      },
      {
        src: '/v2/uynners/au4.jpg'
      }
    ],
    []
  );
  const cardsFreerunning = useMemo(
    () => [
      {
        src: '/v2/uynners/fou1.jpg'
      },
      {
        src: '/v2/uynners/fou2.jpg'
      },
      {
        src: '/v2/uynners/fou3.jpg'
      }
    ],
    []
  );
  const cardsCycling = useMemo(
    () => [
      {
        src: '/v2/uynners/omar1.jpg'
      },
      {
        src: '/v2/uynners/omar2.jpg'
      },
      {
        src: '/v2/uynners/omar3.jpg'
      }
    ],
    []
  );
  const cardsFitness = useMemo(
    () => [
      {
        src: '/v2/uynners/ama1.jpg'
      },
      {
        src: '/v2/uynners/ama2.jpg'
      }
    ],
    []
  );

  return (
    <div>
      <MyHead />

      <div className="section relative full midspace hidden md:block">
        <Image
          src="/v2/uynners/herouynner.jpg"
          alt=""
          width={0}
          height={0}
          sizes="100vw"
          className="w-full absolute top-0 left-0 h-full object-cover object-center"
        />
        <div className="section-wrapper relative text-white">
          <div className="grid gap-8 grid-cols-12 md:gap-10 xl:gap-12">
            <div className="md:col-span-8 col-span-12">
              <div className="h2">{t('P_UYNNERS.SECTION-1.TITLE')}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="section relative">
        <div className="section-wrapper relative">
          <div className="h2 md:hidden">{t('P_UYNNERS.SECTION-1.TITLE')}</div>
          <div className="text-2xl font-medium max-w-[960px] mx-auto text-center mt-6 md:mt-0">
            {t('P_UYNNERS.SECTION-2.TITLE')}
          </div>

          <div className="flex gap-2 flex-wrap justify-center mt-10">
            <button
              onClick={() => scrollToId('winter-sports')}
              className={buttonVariants({ variant: 'dark' })}
            >
              {t('P_UYNNERS.WINTERSPORTS.TITLE-1')}
            </button>

            <button
              onClick={() => scrollToId('free-running')}
              className={buttonVariants({ variant: 'dark' })}
            >
              {t('P_UYNNERS.FREERUNNING.TITLE-1')}
            </button>

            <button
              onClick={() => scrollToId('running')}
              className={buttonVariants({ variant: 'dark' })}
            >
              {t('P_UYNNERS.RUNNING.TITLE-1')}
            </button>

            <button
              onClick={() => scrollToId('cycling')}
              className={buttonVariants({ variant: 'dark' })}
            >
              {t('P_UYNNERS.CYCLING.TITLE-1')}
            </button>

            <button
              onClick={() => scrollToId('sailing')}
              className={buttonVariants({ variant: 'dark' })}
            >
              {t('P_UYNNERS.SAILING.TITLE-1')}
            </button>

            <button
              onClick={() => scrollToId('training')}
              className={buttonVariants({ variant: 'dark' })}
            >
              {t('P_UYNNERS.TRAINING.TITLE-1')}
            </button>
          </div>
        </div>
      </div>

      <div className="section relative bg-blue-50 full">
        <div className="grid gap-8 md:gap-12 xl:gap-20 grid-cols-12">
          <div className="col-span-12 text-center section-wrapper">
            <div className="lead-1">{t('P_UYNNERS.SKITEAMS.TITLE-1')}</div>
            <div className="h4">{t('P_UYNNERS.SKITEAMS.TITLE-2')}</div>
          </div>
          <div className="col-span-12 text-center">
            <Image
              src="/v2/uynners/skiteams.jpg"
              alt=""
              width={0}
              height={0}
              sizes="100vw"
              className="w-full hidden md:block"
            />
            <Image
              src="/v2/uynners/skiteams-mob.jpg"
              alt=""
              width={0}
              height={0}
              sizes="100vw"
              className="w-full md:hidden"
            />
          </div>
        </div>

        <div className="grid gap-8 mt-8 md:gap-12 md:mt-12 xl:gap-20 xl:mt-20 grid-cols-12 section-wrapper">
          <div className="md:col-span-6 col-span-12">
            <div className="text-xl font-medium">
              {t('P_UYNNERS.SKITEAMS.TITLE-3')}
            </div>
            <div className="text-body mt-6">{t('P_UYNNERS.SKITEAMS.DESC')}</div>
          </div>
          <div className="md:col-span-6 col-span-12 grid gap-4 grid-cols-5 items-center">
            <div className="col-span-1">
              <Image
                src="/v2/uynners/logos/1.png"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
            <div className="col-span-1">
              <Image
                src="/v2/uynners/logos/2.png"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
            <div className="col-span-1">
              <Image
                src="/v2/uynners/logos/3.png"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
            <div className="col-span-1">
              <Image
                src="/v2/uynners/logos/4.png"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
            <div className="col-span-1">
              <Image
                src="/v2/uynners/logos/5.png"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
            <div className="col-span-1">
              <Image
                src="/v2/uynners/logos/6.png"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
            <div className="col-span-1">
              <Image
                src="/v2/uynners/logos/7.png"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
            <div className="col-span-1">
              <Image
                src="/v2/uynners/logos/8.png"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
            <div className="col-span-1">
              <Image
                src="/v2/uynners/logos/9.png"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
            <div className="col-span-1">
              <Image
                src="/v2/uynners/logos/10.png"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      <div id="winter-sports" className="section relative">
        <div className="section-wrapper relative">
          <div className="grid gap-8 grid-cols-12 md:gap-10 xl:gap-12">
            <div className="md:col-span-8 col-span-12">
              <div className="h2">{t('P_UYNNERS.WINTERSPORTS.TITLE-1')}</div>
            </div>
            <div className="md:col-span-7 col-span-12">
              <Image
                src="/v2/uynners/ghedina-1.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
            <div className="md:col-span-4 md:col-start-9 col-span-12 self-center">
              <div className="h3">{t('P_UYNNERS.WINTERSPORTS.TITLE-2')}</div>
              <div className="h6">{t('P_UYNNERS.WINTERSPORTS.SUBTITLE')}</div>
              <div className="text-body mt-6">
                {t('P_UYNNERS.WINTERSPORTS.DESC')}
              </div>
            </div>

            <div className="md:col-span-3 col-span-12 self-center">
              <div className="text-2xl font-medium">
                {t('P_UYNNERS.WINTERSPORTS.TITLE-3')}
              </div>

              <div className="flex gap-2 flex-wrap mt-6">
                <Link href="/collections/man/sports-wintersports">
                  <a className={buttonVariants({ variant: 'dark' })}>
                    {t('P_UYNNERS.WINTERSPORTS.BTN-1')}
                  </a>
                </Link>

                <Link href="/collections/woman/sports-wintersports">
                  <a className={buttonVariants({ variant: 'dark' })}>
                    {t('P_UYNNERS.WINTERSPORTS.BTN-2')}
                  </a>
                </Link>
              </div>
            </div>
            <div className="md:col-span-9 md:col-start-4 col-span-12">
              <Swiper
                wrapperTag="ul"
                slidesPerView={1.2}
                spaceBetween={16}
                className=""
                breakpoints={{
                  768: {
                    slidesPerView: 2
                  },
                  1024: {
                    slidesPerView: 2
                  }
                }}
              >
                {cardsWintersports.map((card, idx) => (
                  <SwiperSlide
                    key={idx}
                    tag="li"
                    className="overflow-hidden relative"
                  >
                    <Image
                      src={card.src}
                      alt=""
                      width={0}
                      height={0}
                      sizes="100vw"
                      className="w-full"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      </div>

      <div id="bode" className="section relative">
        <div className="section-wrapper relative">
          <div className="grid gap-8 grid-cols-12 md:gap-10 xl:gap-12">
            <div className="md:col-span-7 col-span-12">
              <Image
                src="/v2/uynners/bode-1.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
            <div className="md:col-span-4 md:col-start-9 col-span-12 self-center">
              <div className="h3">{t('P_UYNNERS.WINTERSPORTS.TITLE-BODE')}</div>
              <div className="h6">
                {t('P_UYNNERS.WINTERSPORTS.SUBTITLE-BODE')}
              </div>
              <div className="text-body mt-6">
                {t('P_UYNNERS.WINTERSPORTS.DESC-BODE')}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="austria" className="section relative">
        <div className="section-wrapper relative">
          <div className="grid gap-8 grid-cols-12 md:gap-10 xl:gap-12">
            <div className="md:col-span-7 col-span-12">
              <Image
                src="/v2/uynners/au1.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
            <div className="md:col-span-4 md:col-start-9 col-span-12 self-center">
              <div className="h3">{t('P_UYNNERS.AUSTRIA.TITLE')}</div>
              <div className="text-body mt-6">
                {t('P_UYNNERS.AUSTRIA.CONTENT')}
              </div>
            </div>

            <div className="md:col-span-11 md:col-start-2 col-span-12">
              <Swiper
                wrapperTag="ul"
                slidesPerView={1.2}
                spaceBetween={16}
                className=""
                breakpoints={{
                  768: {
                    slidesPerView: 3
                  },
                  1024: {
                    slidesPerView: 3
                  }
                }}
              >
                {cardsAustria.map((card, idx) => (
                  <SwiperSlide
                    key={idx}
                    tag="li"
                    className="overflow-hidden relative"
                  >
                    <Image
                      src={card.src}
                      alt=""
                      width={0}
                      height={0}
                      sizes="100vw"
                      className="w-full"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            {/* <div className="md:col-span-5 col-span-12 self-center">
              <div className="h5">{t('P_UYNNERS.CYCLING.TITLE-3')}</div>
              <div className="text-body mt-6">
                {t('P_UYNNERS.CYCLING.DESC-2')}
              </div>
              <div className="flex gap-2 flex-wrap mt-6">
                <Link href="/collections/man/sports-cycling">
                  <a className={buttonVariants({ variant: 'dark' })}>
                    {t('P_UYNNERS.CYCLING.BTN-1')}
                  </a>
                </Link>
                <Link href="/collections/woman/sports-cycling">
                  <a className={buttonVariants({ variant: 'dark' })}>
                    {t('P_UYNNERS.CYCLING.BTN-2')}
                  </a>
                </Link>
              </div>
            </div>

            <div className="md:col-span-7 col-span-12">
              <Image
                src="/v2/uynners/omar-shop.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div> */}
          </div>
        </div>
      </div>

      <div id="free-running" className="section relative">
        <div className="section-wrapper relative">
          <div className="grid gap-8 grid-cols-12 md:gap-10 xl:gap-12">
            <div className="md:col-span-8 col-span-12">
              <div className="h2">{t('P_UYNNERS.FREERUNNING.TITLE-1')}</div>
            </div>
            <div className="video-wrapper aspect-landscape md:col-span-7 col-span-12">
              <VideoPlayer
                src="https://vimeo.com/847334002/191499624d"
                autoPlay
              />
            </div>
            <div className="md:col-span-4 md:col-start-9 col-span-12 self-center">
              <div className="h3">{t('P_UYNNERS.FREERUNNING.TITLE-2')}</div>
              <div className="text-body mt-6">
                {t('P_UYNNERS.FREERUNNING.DESC')}
              </div>
            </div>

            <div className="md:col-span-11 md:col-start-2 col-span-12">
              <Swiper
                wrapperTag="ul"
                slidesPerView={1.2}
                spaceBetween={16}
                className=""
                breakpoints={{
                  768: {
                    slidesPerView: 3
                  },
                  1024: {
                    slidesPerView: 3
                  }
                }}
              >
                {cardsFreerunning.map((card, idx) => (
                  <SwiperSlide
                    key={idx}
                    tag="li"
                    className="overflow-hidden relative"
                  >
                    <Image
                      src={card.src}
                      alt=""
                      width={0}
                      height={0}
                      sizes="100vw"
                      className="w-full"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            <div className="col-span-12 text-center">
              <div className="mx-auto max-w-[860px] text-xl font-medium">
                {t('P_UYNNERS.FREERUNNING.TITLE-CENTER')}
              </div>
              <div className="mx-auto max-w-[860px] text-xl font-medium">
                {t('P_UYNNERS.FREERUNNING.TITLE-CENTER-B')}
              </div>
            </div>

            <div className="md:col-span-5 md:col-start-2 col-span-12">
              <Image
                src="/v2/uynners/2833.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
              <div className="mt-6 space-y-6">
                <div className="text-body mt-6 max-w-[480px]">
                  {t('P_UYNNERS.FREERUNNING.BOX-ONE.TITLE')}
                </div>
                <div className="flex gap-2 flex-wrap mt-6">
                  <Link href="/collections/man/sports-running">
                    <a className={buttonVariants({ variant: 'dark' })}>
                      {t('P_UYNNERS.FREERUNNING.BOX-ONE.BTN-1')}
                    </a>
                  </Link>
                  <Link href="/collections/woman/sports-running">
                    <a className={buttonVariants({ variant: 'dark' })}>
                      {t('P_UYNNERS.FREERUNNING.BOX-ONE.BTN-2')}
                    </a>
                  </Link>
                </div>
              </div>
            </div>
            <div className="md:col-span-5 col-span-12">
              <Image
                src="/v2/uynners/2834.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
              <div className="mt-6 space-y-6">
                <div className="text-body mt-6 max-w-[480px]">
                  {t('P_UYNNERS.FREERUNNING.BOX-TWO.TITLE')}
                </div>
                <div className="flex gap-2 flex-wrap mt-6">
                  <Link href="/collections/man/shoes">
                    <a className={buttonVariants({ variant: 'dark' })}>
                      {t('P_UYNNERS.FREERUNNING.BOX-TWO.BTN-1')}
                    </a>
                  </Link>
                  <Link href="/collections/woman/shoes">
                    <a className={buttonVariants({ variant: 'dark' })}>
                      {t('P_UYNNERS.FREERUNNING.BOX-TWO.BTN-2')}
                    </a>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="running" className="section full bg-sand-50 relative">
        <div className="section-wrapper relative">
          <div className="grid gap-8 grid-cols-12 md:gap-10 xl:gap-12">
            <div className="col-span-12">
              <div className="h2">{t('P_UYNNERS.RUNNING.TITLE-1')}</div>
            </div>
            <div className="md:col-span-6 col-span-12">
              <Image
                src="/v2/uynners/run1.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
              <div className="mt-6">
                <div className="h4">{t('P_UYNNERS.RUNNING.BOX-ONE.TITLE')}</div>
                <div className="text-body mt-3">
                  {t('P_UYNNERS.RUNNING.BOX-ONE.DESC')}
                </div>
              </div>
            </div>
            <div className="md:col-span-6 col-span-12">
              <Image
                src="/v2/uynners/run2.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
              <div className="mt-6">
                <div className="h4">{t('P_UYNNERS.RUNNING.BOX-TWO.TITLE')}</div>
                <div className="text-body mt-3">
                  {t('P_UYNNERS.RUNNING.BOX-TWO.DESC')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="cycling" className="section relative">
        <div className="section-wrapper relative">
          <div className="grid gap-8 grid-cols-12 md:gap-10 xl:gap-12">
            <div className="md:col-span-8 col-span-12">
              <div className="h2">{t('P_UYNNERS.CYCLING.TITLE-1')}</div>
            </div>
            <div className="md:col-span-7 col-span-12">
              <Image
                src="/v2/uynners/omar-1.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
            <div className="md:col-span-4 md:col-start-9 col-span-12 self-center">
              <div className="h3">{t('P_UYNNERS.CYCLING.TITLE-2')}</div>
              <div className="text-body mt-6">
                {t('P_UYNNERS.CYCLING.DESC')}
              </div>
            </div>

            <div className="md:col-span-11 md:col-start-2 col-span-12">
              <Swiper
                wrapperTag="ul"
                slidesPerView={1.2}
                spaceBetween={16}
                className=""
                breakpoints={{
                  768: {
                    slidesPerView: 3
                  },
                  1024: {
                    slidesPerView: 3
                  }
                }}
              >
                {cardsCycling.map((card, idx) => (
                  <SwiperSlide
                    key={idx}
                    tag="li"
                    className="overflow-hidden relative"
                  >
                    <Image
                      src={card.src}
                      alt=""
                      width={0}
                      height={0}
                      sizes="100vw"
                      className="w-full"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            <div className="md:col-span-5 col-span-12 self-center">
              <div className="h5">{t('P_UYNNERS.CYCLING.TITLE-3')}</div>
              <div className="text-body mt-6">
                {t('P_UYNNERS.CYCLING.DESC-2')}
              </div>
              <div className="flex gap-2 flex-wrap mt-6">
                <Link href="/collections/man/sports-cycling">
                  <a className={buttonVariants({ variant: 'dark' })}>
                    {t('P_UYNNERS.CYCLING.BTN-1')}
                  </a>
                </Link>
                <Link href="/collections/woman/sports-cycling">
                  <a className={buttonVariants({ variant: 'dark' })}>
                    {t('P_UYNNERS.CYCLING.BTN-2')}
                  </a>
                </Link>
              </div>
            </div>

            <div className="md:col-span-7 col-span-12">
              <Image
                src="/v2/uynners/omar-shop.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      <div id="trinx" className="section relative">
        <div className="section-wrapper relative">
          <div className="grid gap-8 grid-cols-12 md:gap-10 xl:gap-12">
            <div className="md:col-span-7 col-span-12">
              <Image
                src="/v2/uynners/trinx1.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
            <div className="md:col-span-4 md:col-start-9 col-span-12 self-center">
              <div className="h3">{t('P_UYNNERS.TRINX.TITLE')}</div>
              <div className="text-body mt-6">
                {t('P_UYNNERS.TRINX.CONTENT')}
              </div>
            </div>

            <div className="md:col-span-3 col-span-12 self-center">
              <div className="text-2xl font-medium">
                {t('P_UYNNERS.TRINX.MORE')}
              </div>
            </div>
            <div className="md:col-span-9 md:col-start-4 col-span-12">
              <Swiper
                wrapperTag="ul"
                slidesPerView={1.2}
                spaceBetween={16}
                className=""
                breakpoints={{
                  768: {
                    slidesPerView: 2
                  },
                  1024: {
                    slidesPerView: 2
                  }
                }}
              >
                {cardsTrinx.map((card, idx) => (
                  <SwiperSlide
                    key={idx}
                    tag="li"
                    className="overflow-hidden relative"
                  >
                    <Image
                      src={card.src}
                      alt=""
                      width={0}
                      height={0}
                      sizes="100vw"
                      className="w-full"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      </div>

      <div id="sailing" className="section full bg-sand-50 relative">
        <div className="section-wrapper relative">
          <div className="grid gap-8 grid-cols-12 md:gap-10 xl:gap-12">
            <div className="col-span-12">
              <div className="h2">{t('P_UYNNERS.SAILING.TITLE-1')}</div>
            </div>
            <div className="md:col-span-4 col-span-12">
              <Image
                src="/v2/uynners/quantum-racing.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
              <div className="mt-6">
                <div className="h4">{t('P_UYNNERS.SAILING.BOX-ONE.TITLE')}</div>
                <div className="text-body mt-3">
                  {t('P_UYNNERS.SAILING.BOX-ONE.DESC')}
                </div>
              </div>
            </div>
            <div className="md:col-span-4 col-span-12">
              <Image
                src="/v2/uynners/giuliana-sailing.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
              <div className="mt-6">
                <div className="h4">{t('P_UYNNERS.SAILING.BOX-TWO.TITLE')}</div>
                <div className="text-body mt-3">
                  {t('P_UYNNERS.SAILING.BOX-TWO.DESC')}
                </div>
              </div>
            </div>
            <div className="md:col-span-4 col-span-12">
              <Image
                src="/v2/uynners/cecilia.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
              <div className="mt-6">
                <div className="h4">
                  {t('P_UYNNERS.SAILING.BOX-THREE.TITLE')}
                </div>
                <div className="text-body mt-3">
                  {t('P_UYNNERS.SAILING.BOX-THREE.DESC')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="training" className="section relative">
        <div className="section-wrapper relative">
          <div className="grid gap-8 grid-cols-12 md:gap-10 xl:gap-12">
            <div className="md:col-span-8 col-span-12">
              <div className="h2">{t('P_UYNNERS.TRAINING.TITLE-1')}</div>
            </div>
            <div className="video-wrapper aspect-landscape md:col-span-7 col-span-12">
              <VideoPlayer
                src="https://vimeo.com/847336628/4f9fa9cf78"
                autoPlay
              />
            </div>
            <div className="md:col-span-4 md:col-start-9 col-span-12 self-center">
              <div className="h3">{t('P_UYNNERS.TRAINING.TITLE-2')}</div>
              <div className="text-body mt-6">
                {t('P_UYNNERS.TRAINING.DESC')}
              </div>
            </div>

            <div className="md:col-span-3 col-span-12 self-center">
              <div className="text-2xl font-medium">
                {t('P_UYNNERS.TRAINING.TITLE-3')}
              </div>

              <div className="flex gap-2 flex-wrap mt-6">
                <Link href="/collections/man/sports-fitness-training">
                  <a className={buttonVariants({ variant: 'dark' })}>
                    {t('P_UYNNERS.TRAINING.BTN-1')}
                  </a>
                </Link>
                <Link href="/collections/woman/sports-fitness-training">
                  <a className={buttonVariants({ variant: 'dark' })}>
                    {t('P_UYNNERS.TRAINING.BTN-2')}
                  </a>
                </Link>
              </div>
            </div>
            <div className="md:col-span-9 md:col-start-4 col-span-12">
              <Swiper
                wrapperTag="ul"
                slidesPerView={1.2}
                spaceBetween={16}
                className=""
                breakpoints={{
                  768: {
                    slidesPerView: 2
                  },
                  1024: {
                    slidesPerView: 2
                  }
                }}
              >
                {cardsFitness.map((card, idx) => (
                  <SwiperSlide
                    key={idx}
                    tag="li"
                    className="overflow-hidden relative"
                  >
                    <Image
                      src={card.src}
                      alt=""
                      width={0}
                      height={0}
                      sizes="100vw"
                      className="w-full"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            <div className="md:col-span-7 col-span-12">
              <Image
                src="/v2/uynners/scalia1.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
            <div className="md:col-span-4 md:col-start-9 col-span-12 self-center">
              <div className="h3">{t('P_UYNNERS.SCALIA.TITLE')}</div>
              <div className="text-body mt-6">
                {t('P_UYNNERS.SCALIA.CONTENT')}
              </div>
            </div>
          </div>
        </div>
      </div>

      <StaticPagesCards />
    </div>
  );
};

export default UynnersPage;
